import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, SignOut, Sparkle, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, fmtMoney, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import PayChatPanel from "../components/PayChatPanel";

const ROLE_LABEL = { super_admin: "Super Admin", hr_admin: "HR Admin", manager: "Manager", employee: "Employee" };
const DEPARTMENTS = ["Admin", "CSR", "Sales", "Quoting Team", "Service Team"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function PayForm({ member, onSaved }) {
  const [p, setP] = useState(member.pay_params);
  const [changeNote, setChangeNote] = useState("");
  const [effDate, setEffDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [effTime, setEffTime] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setP(member.pay_params), [member]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${member.id}/pay-params`, {
        pay_type: p.pay_type,
        hourly_rate: parseFloat(p.hourly_rate) || 0,
        annual_salary: parseFloat(p.annual_salary) || 0,
        pay_schedule: p.pay_schedule,
        standard_hours_per_week: parseFloat(p.standard_hours_per_week) || 40,
        overtime_multiplier: parseFloat(p.overtime_multiplier) || 1.5,
        pto_paid: p.pto_paid,
        sick_paid_requires_note: p.sick_paid_requires_note,
        notes: p.notes || "",
        change_note: changeNote,
        effective_date: effTime ? `${effDate}T${effTime}` : effDate,
      });
      toast.success("Pay parameters saved — logged to audit trail");
      setChangeNote("");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-4" data-testid="pay-params-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Pay type</Label>
          <Select value={p.pay_type} onValueChange={(v) => setP({ ...p, pay_type: v })}>
            <SelectTrigger data-testid="pay-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="salary">Salary</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label>Pay schedule</Label>
          <Select value={p.pay_schedule} onValueChange={(v) => setP({ ...p, pay_schedule: v })}>
            <SelectTrigger data-testid="pay-schedule-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="biweekly">Bi-weekly</SelectItem><SelectItem value="semimonthly">Semi-monthly (1st & 15th, lagging)</SelectItem><SelectItem value="monthly">Monthly (15th, prior month)</SelectItem></SelectContent>
          </Select>
        </div>
        {p.pay_type === "hourly" ? (
          <div><Label>Hourly rate ($)</Label><Input data-testid="hourly-rate-input" type="number" step="0.01" className="mt-1.5" value={p.hourly_rate} onChange={(e) => setP({ ...p, hourly_rate: e.target.value })} /></div>
        ) : (
          <div><Label>Annual salary ($)</Label><Input data-testid="annual-salary-input" type="number" step="100" className="mt-1.5" value={p.annual_salary} onChange={(e) => setP({ ...p, annual_salary: e.target.value })} /></div>
        )}
        <div><Label>Standard hours / week</Label><Input data-testid="std-hours-input" type="number" className="mt-1.5" value={p.standard_hours_per_week} onChange={(e) => setP({ ...p, standard_hours_per_week: e.target.value })} /></div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
        <label className="flex items-center gap-2.5 text-sm"><Switch data-testid="pto-paid-switch" checked={p.pto_paid} onCheckedChange={(v) => setP({ ...p, pto_paid: v })} /> PTO is paid</label>
        <label className="flex items-center gap-2.5 text-sm"><Switch data-testid="sick-note-switch" checked={p.sick_paid_requires_note} onCheckedChange={(v) => setP({ ...p, sick_paid_requires_note: v })} /> Paid sick requires doctor's note</label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Effective date <span className="text-red-500">*</span></Label>
          <Input data-testid="pay-effective-date-input" type="date" className="mt-1.5" value={effDate} onChange={(e) => setEffDate(e.target.value)} />
          <p className="text-xs text-muted-foreground mt-1">When the change actually took (or takes) effect — back-date or future-date as needed. Pay periods are calculated with the rate in force on each day.</p>
        </div>
        <div>
          <Label>Effective time <span className="text-muted-foreground text-xs">(optional)</span></Label>
          <Input data-testid="pay-effective-time-input" type="time" className="mt-1.5" value={effTime} onChange={(e) => setEffTime(e.target.value)} />
          <p className="text-xs text-muted-foreground mt-1">Kept for the record only — calculations use the date.</p>
        </div>
      </div>
      <div>
        <Label>Reason for this change <span className="text-red-500">*</span></Label>
        <Textarea data-testid="pay-change-note-input" className="mt-1.5" rows={2}
          placeholder="e.g. Annual raise approved 9/1, corrected schedule per offer letter…"
          value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
        <p className="text-xs text-muted-foreground mt-1">Required — every pay change is recorded with its note in the pay history and audit trail.</p>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Effective: {p.effective_date ? fmtDate(p.effective_date) : "—"}</p>
        <Button data-testid="save-pay-params-btn" onClick={save} disabled={busy || !changeNote.trim() || !effDate} className="bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">{busy ? "Saving…" : "Save pay parameters"}</Button>
      </div>
    </div>
  );
}

export default function MemberDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [member, setMember] = useState(null);
  const [paycheck, setPaycheck] = useState(null);
  const [payHistory, setPayHistory] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [statesDraft, setStatesDraft] = useState([]);
  const [memberReviews, setMemberReviews] = useState([]);
  const [expandedReview, setExpandedReview] = useState(null);
  const [emailDraft, setEmailDraft] = useState("");
  const [hireDraft, setHireDraft] = useState("");
  const [allUsers, setAllUsers] = useState([]);

  const load = useCallback(() => {
    api.get(`/users/${id}`).then((r) => { setMember(r.data); setStatesDraft(r.data.licensed_states || []); setEmailDraft(r.data.email); setHireDraft((r.data.hire_date || "").slice(0, 10)); }).catch(() => navigate("/team"));
    if (hasPerm("pay.view")) {
      api.get(`/payroll/next-paycheck/${id}`).then((r) => setPaycheck(r.data)).catch(() => {});
      api.get(`/users/${id}/pay-history`).then((r) => setPayHistory(r.data)).catch(() => {});
    }
    if (hasPerm("audit.view")) {
      api.get("/audit", { params: { target_user_id: id } }).then((r) => setAuditLogs(r.data)).catch(() => {});
    }
    if (hasPerm("users.manage")) {
      api.get("/reviews", { params: { user_id: id } }).then((r) => setMemberReviews(r.data)).catch(() => {});
      api.get("/users").then((r) => setAllUsers(r.data)).catch(() => {});
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!member) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const changeRole = async (role) => {
    try {
      await api.put(`/users/${id}/role`, { role });
      toast.success(`Role changed to ${ROLE_LABEL[role]}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Role change failed");
    }
  };

  const forceLogout = async () => {
    try {
      const { data } = await api.delete(`/users/${id}/sessions`);
      toast.success(`Force logged out — ${data.revoked} active session(s) revoked`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Force logout failed");
    }
  };

  const viewAs = async () => {
    try {
      const { data } = await api.post(`/auth/impersonate/${id}`);
      localStorage.setItem("cw_admin_token", localStorage.getItem("cw_token"));
      localStorage.setItem("cw_token", data.token);
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start view-as session");
    }
  };

  const changeDepartment = async (department) => {
    try {
      await api.put(`/users/${id}/profile`, { department });
      toast.success(`Department set to ${department}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Department change failed");
    }
  };

  const changeReviewer = async (reviewer_id) => {
    try {
      await api.put(`/users/${id}/reviewer`, { reviewer_id: reviewer_id === "none" ? null : reviewer_id });
      toast.success(reviewer_id === "none" ? "Default reviewer cleared" : "Default reviewer set — pending reviews reassigned");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reviewer change failed");
    }
  };

  const saveHireDate = async () => {
    try {
      await api.put(`/users/${id}/profile`, { hire_date: hireDraft });
      toast.success("Hire date saved — logged to audit trail");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hire date change failed");
    }
  };

  const toggleActive = async () => {
    try {
      if (member.is_active) {
        const { data } = await api.post(`/users/${id}/deactivate`);
        toast.success(data.licensing_reviewer_reassigned_to
          ? `Deactivated — licensing reviewer auto-reassigned to ${data.licensing_reviewer_reassigned_to}`
          : "Account deactivated and sessions revoked");
      } else {
        await api.post(`/users/${id}/reactivate`);
        toast.success("Account reactivated");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const toggleState = (s) => {
    setStatesDraft((d) => d.includes(s) ? d.filter((x) => x !== s) : [...d, s]);
  };

  const saveEmail = async () => {
    try {
      await api.put(`/users/${id}/email`, { email: emailDraft });
      toast.success("Sign-in email updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update email");
    }
  };

  const saveStates = async () => {
    try {
      await api.put(`/users/${id}/licensed-states`, { states: statesDraft });
      toast.success("Licensed states updated — member notified to upload licenses");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update states");
    }
  };

  const photo = member.photo_file_path ? fileUrl(member.photo_file_path) : member.avatar_url;

  return (
    <div className="space-y-8">
      <button data-testid="back-to-team-btn" onClick={() => navigate("/team")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={15} /> Team directory
      </button>

      <div className="flex flex-wrap items-center gap-5">
        <Avatar className="h-16 w-16 border border-border">
          <AvatarImage src={photo} />
          <AvatarFallback className="bg-slate-200">{member.full_name?.split(" ").map((w) => w[0]).join("").slice(0, 2)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="member-name">{member.full_name} {!member.is_active && <Badge className="bg-red-100 text-red-700 align-middle ml-1">deactivated</Badge>}</h1>
          <p className="text-sm text-muted-foreground">{member.job_title} · {member.email}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {hasPerm("roles.manage") && member.id !== user.id ? (
            <Select value={member.role} onValueChange={changeRole}>
              <SelectTrigger data-testid="member-role-select" className="w-40 bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(ROLE_LABEL).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Badge className="bg-indigo-100 text-indigo-800">{ROLE_LABEL[member.role]}</Badge>
          )}
          {hasPerm("users.manage") && member.id !== user.id && (
            <Button data-testid="view-as-btn" variant="outline" onClick={viewAs} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800">
              <Eye size={15} className="mr-1.5" /> View as
            </Button>
          )}
          {hasPerm("sessions.revoke") && (
            <Button data-testid="force-logout-btn" variant="outline" onClick={forceLogout} className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
              <SignOut size={15} className="mr-1.5" /> Force logout
            </Button>
          )}
          {hasPerm("users.manage") && member.id !== user.id && (
            <Button data-testid="toggle-active-btn" variant="outline" onClick={toggleActive}
              className={member.is_active ? "border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}>
              {member.is_active ? "Deactivate" : "Reactivate"}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-secondary flex-wrap h-auto">
          <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
          {hasPerm("pay.manage") && <TabsTrigger data-testid="tab-pay" value="pay">Pay setup</TabsTrigger>}
          {hasPerm("ai.chat") && <TabsTrigger data-testid="tab-ai" value="ai"><Sparkle size={13} className="mr-1" /> AI setup</TabsTrigger>}
          {hasPerm("users.manage") && <TabsTrigger data-testid="tab-reviews" value="reviews">Reviews</TabsTrigger>}
          {hasPerm("audit.view") && <TabsTrigger data-testid="tab-history" value="history">History</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-md p-6 space-y-3 text-sm" data-testid="member-profile-card">
              <h3 className="font-heading font-semibold tracking-tight mb-1">Profile</h3>
              {[["Date of birth", fmtDate(member.date_of_birth)],
                ["Personal email", member.personal_email || "—"],
                ["Phone", member.phone || "—"],
                ["Mailing address", member.mailing_address?.street ? `${member.mailing_address.street}, ${member.mailing_address.city}, ${member.mailing_address.state} ${member.mailing_address.zip_code}` : "—"],
                ["Profile verified", member.profile_confirmed_at ? fmtDate(member.profile_confirmed_at) : "Never"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span></div>
              ))}
              {user.role === "super_admin" || (hasPerm("users.manage") && !member.hire_date) ? (
                <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
                  <span className="text-muted-foreground shrink-0">Hire date {member.hire_date ? "(locked)" : ""}</span>
                  <div className="flex gap-2">
                    <Input data-testid="member-hire-date-input" type="date" className="h-9 w-40" value={hireDraft} onChange={(e) => setHireDraft(e.target.value)} />
                    <Button data-testid="member-hire-date-save-btn" size="sm" variant="outline" className="h-9" onClick={saveHireDate} disabled={!hireDraft || hireDraft === (member.hire_date || "").slice(0, 10)}>Save</Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Hire date</span><span className="text-right">{fmtDate(member.hire_date)}</span></div>
              )}
              {member.needs_profile_verification && <Badge className="bg-amber-100 text-amber-800">6-month verification overdue</Badge>}
              {hasPerm("users.manage") && (
                <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
                  <span className="text-muted-foreground shrink-0">Sign-in email</span>
                  <div className="flex gap-2 min-w-0">
                    <Input data-testid="member-email-input" type="email" className="h-9 w-56" value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} />
                    <Button data-testid="member-email-save-btn" size="sm" variant="outline" className="h-9" onClick={saveEmail} disabled={!emailDraft || emailDraft === member.email}>Save</Button>
                  </div>
                </div>
              )}
              {hasPerm("users.manage") && (
                <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
                  <span className="text-muted-foreground">Department</span>
                  <Select value={member.department || ""} onValueChange={changeDepartment}>
                    <SelectTrigger data-testid="member-department-select" className="w-44"><SelectValue placeholder="Set department" /></SelectTrigger>
                    <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {hasPerm("users.manage") && member.role !== "super_admin" && (
                <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
                  <span className="text-muted-foreground shrink-0">Performance reviewer</span>
                  <Select value={member.reviewer_id || "none"} onValueChange={changeReviewer}>
                    <SelectTrigger data-testid="member-reviewer-select" className="w-44"><SelectValue placeholder="Default (unassigned)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Default (super admin)</SelectItem>
                      {allUsers.filter((u) => u.is_active && u.id !== member.id && (u.role === "super_admin" || (u.permission_overrides || {})["reviews.complete"] === true)).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {hasPerm("users.manage") && (
                <div className="pt-2 border-t border-border" data-testid="licensed-states-editor">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground">Licensed states (writes insurance in)</span>
                    <Button data-testid="save-states-btn" size="sm" variant="outline" onClick={saveStates}
                      disabled={JSON.stringify([...statesDraft].sort()) === JSON.stringify([...(member.licensed_states || [])].sort())}>
                      Save states
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {US_STATES.map((s) => (
                      <button key={s} data-testid={`state-chip-${s}`} onClick={() => toggleState(s)}
                        className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${
                          statesDraft.includes(s) ? "bg-indigo-600 text-white border-indigo-600" : "bg-card text-muted-foreground border-border hover:border-indigo-400"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Member is emailed to upload a license for each newly added state.</p>
                </div>
              )}
              {!hasPerm("users.manage") && member.licensed_states?.length > 0 && (
                <div className="flex justify-between gap-4 pt-2 border-t border-border">
                  <span className="text-muted-foreground">Licensed states</span>
                  <span className="text-right">{member.licensed_states.join(", ")}</span>
                </div>
              )}
            </div>
            <div className="bg-card border border-border rounded-md p-6 space-y-3 text-sm">
              <h3 className="font-heading font-semibold tracking-tight mb-1">Balances & next paycheck</h3>
              <div className="flex justify-between"><span className="text-muted-foreground">PTO balance</span><span>{member.pto_balance_hours}h</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sick balance</span><span>{member.sick_balance_hours}h</span></div>
              {paycheck && (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Pay period</span><span>{fmtDate(paycheck.period_start)} → {fmtDate(paycheck.period_end)}</span></div>
                  <div className="flex justify-between font-semibold border-t border-border pt-2.5"><span>Gross estimate</span><span className="text-indigo-700" data-testid="member-gross-estimate">{fmtMoney(paycheck.gross_estimate)}</span></div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {hasPerm("pay.manage") && (
          <TabsContent value="pay" className="mt-6 space-y-6">
            <PayForm member={member} onSaved={load} />
            <div className="bg-card border border-border rounded-md">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Pay change history</h3></div>
              <div className="divide-y divide-border" data-testid="pay-history-list">
                {payHistory.map((h) => (
                  <div key={h.id} className="px-6 py-3.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="font-medium">{h.changed_by_name} <span className="text-xs text-muted-foreground">via {h.source === "ai_chat" ? "AI assistant" : "manual edit"}</span></span>
                      <span className="text-xs text-muted-foreground">recorded {new Date(h.timestamp).toLocaleString()}</span>
                    </div>
                    {h.effective_date && <p className="text-xs mt-0.5"><span className="text-muted-foreground">Effective:</span> <strong>{h.effective_date.includes("T") ? new Date(h.effective_date).toLocaleString() : fmtDate(h.effective_date)}</strong></p>}
                    {h.changes.map((c, i) => (
                      <p key={i} className="text-xs font-mono mt-1">{c.field}: <span className="text-red-600 line-through">{String(c.old ?? "—")}</span> → <span className="text-emerald-700 font-semibold">{String(c.new)}</span></p>
                    ))}
                    {h.note && <p className="text-xs text-muted-foreground mt-1">Note: "{h.note}"</p>}
                  </div>
                ))}
                {payHistory.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">No pay changes recorded yet.</p>}
              </div>
            </div>
          </TabsContent>
        )}

        {hasPerm("ai.chat") && (
          <TabsContent value="ai" className="mt-6">
            <PayChatPanel employee={member} onApplied={load} />
          </TabsContent>
        )}

        {hasPerm("users.manage") && (
          <TabsContent value="reviews" className="mt-6">
            <div className="bg-card border border-border rounded-md" data-testid="member-reviews-list">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="font-heading font-semibold tracking-tight">Performance review history</h3>
                <p className="text-xs text-muted-foreground mt-0.5">One review per year (opens each June 1). Track growth across cycles.</p>
              </div>
              <div className="divide-y divide-border">
                {memberReviews.map((r) => (
                  <div key={r.id}>
                    <button data-testid={`member-review-row-${r.cycle_year}`} onClick={() => setExpandedReview(expandedReview === r.id ? null : r.id)}
                      className="w-full px-6 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-muted transition-colors">
                      <div>
                        <p className="text-sm font-medium">{r.cycle_year} annual review</p>
                        <p className="text-xs text-muted-foreground">
                          {r.status === "approved" ? `Completed by ${r.submitted_by_name} · approved by ${r.approved_by_name} on ${fmtDate(r.approved_at)}` : `Due ${fmtDate(r.due_date)}`}
                        </p>
                      </div>
                      <Badge className={{ pending: "bg-amber-100 text-amber-800", submitted: "bg-sky-100 text-sky-800", approved: "bg-emerald-100 text-emerald-800" }[r.status]}>{r.status}</Badge>
                    </button>
                    {expandedReview === r.id && (
                      <div className="px-6 pb-5 pt-1 bg-muted/50 space-y-3 text-sm" data-testid={`member-review-detail-${r.cycle_year}`}>
                        {r.status === "pending" ? (
                          <p className="text-muted-foreground text-xs">Not completed yet — manage it from the Reviews page.</p>
                        ) : (
                          <>
                            {r.questions.map((q, i) => (
                              <div key={i}>
                                <p className="text-xs font-semibold text-muted-foreground">{i + 1}. {q.question}</p>
                                <p className="mt-0.5">{q.answer || "—"}</p>
                              </div>
                            ))}
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground">Meeting notes</p>
                              <p className="mt-0.5 whitespace-pre-wrap">{r.notes || "—"}</p>
                            </div>
                            {r.super_admin_comment && (
                              <p className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 text-xs">
                                Super admin ({r.approved_by_name}): "{r.super_admin_comment}"
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {memberReviews.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">No reviews yet — the first one opens June 1.</p>}
              </div>
            </div>
          </TabsContent>
        )}

        {hasPerm("audit.view") && (
          <TabsContent value="history" className="mt-6">
            <div className="bg-card border border-border rounded-md divide-y divide-border" data-testid="member-audit-list">
              {auditLogs.map((l) => (
                <div key={l.id} className="px-6 py-3.5 text-sm">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span><Badge className="bg-slate-100 text-slate-700 mr-2">{l.action}</Badge>{l.actor_name}</span>
                    <span className="text-xs text-muted-foreground">{new Date(l.timestamp).toLocaleString()}</span>
                  </div>
                  {l.detail && <p className="text-xs text-muted-foreground mt-1">{l.detail}</p>}
                </div>
              ))}
              {auditLogs.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">No audit entries for this member.</p>}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
